import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Download,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { Tour } from '../types';
import { fetchTourById } from '../services/tourService';
import { getBrochureDownloadUrl } from '../utils/brochureUtils';
import WhatsAppGroupButton from '../components/ui/WhatsAppGroupButton';

const TourDetailsPage: React.FC = () => {
  const { tourId } = useParams<{ tourId: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTour = async () => {
      if (!tourId) {
        setError('Tour ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const tourData = await fetchTourById(tourId);
        
        if (!tourData) {
          setError('Tour not found');
        } else {
          setTour(tourData);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load tour';
        setError(errorMessage);
        console.error('Error loading tour:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTour();
  }, [tourId]);

  // Calculate days until deadline
  const calculateDaysToDeadline = (deadline?: string) => {
    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Format price to Indian Rupees
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-deep-blue mx-auto mb-4"></div>
          <p className="body-large text-secondary-slate">Loading tour details...</p>
        </div>
      </div>
    );
  }

  if (error || !tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-error mx-auto mb-4" />
          <h1 className="heading-primary text-error mb-4">Tour Not Found</h1>
          <p className="body-large text-secondary-slate mb-6">{error}</p>
          <Link to="/" className="btn btn-primary">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const daysToDeadline = calculateDaysToDeadline(tour.deadline);
  const isUrgent = daysToDeadline !== null && daysToDeadline <= 7;
  const isExpired = daysToDeadline !== null && daysToDeadline < 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen gradient-cream-br"
    >
      {/* Header */}
      <div className="bg-primary-deep-blue text-white py-8">
        <div className="container">
          <div className="flex items-center gap-4 mb-6">
            <h1 className="heading-primary">Tour Details</h1>
          </div>
        </div>
      </div>

      <div className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Main Content */}
          <div className="lg:col-span-2">
            {/* Tour Banner Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative overflow-hidden rounded-2xl mb-8"
            >
              <img
                src={tour.imageUrl}
                alt={tour.tourName}
                className="w-full h-96 object-cover"
              />
              
              {/* Badges */}
              <div className="absolute top-6 left-6 flex flex-col space-y-3">
                {tour.isNew && (
                  <span className="badge-accent-lg">
                    <Sparkles size={16} />
                    New Tour
                  </span>
                )}
                
                {isUrgent && !isExpired && (
                  <span className="px-4 py-2 bg-error text-white font-semibold rounded-full shadow-lg animate-pulse">
                    {daysToDeadline} day{daysToDeadline !== 1 ? 's' : ''} left
                  </span>
                )}

                {typeof tour.urgencySlots === 'number' && tour.urgencySlots > 0 && (
                  <span className="px-4 py-2 bg-warning text-white font-semibold rounded-full shadow-lg">
                    Only {tour.urgencySlots} slots left
                  </span>
                )}
              </div>
            </motion.div>

            {/* Tour Title and Countries */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg mb-8"
            >
              <h2 className={`heading-secondary text-neutral-charcoal ${tour.tagline?.trim() ? 'mb-3' : 'mb-6'}`}>{tour.tourName}</h2>

              {tour.tagline?.trim() && (
                <p className="body-large text-primary-gold font-medium mb-4">
                  {tour.tagline}
                </p>
              )}
              
              <div className="flex items-center text-secondary-slate mb-6">
                <MapPin size={20} className="mr-3 text-primary-emerald" />
                <span className="body-large">{tour.destination}</span>
              </div>

              <div className="body-large text-secondary-slate leading-relaxed prose prose-lg max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {tour.description}
                </ReactMarkdown>
              </div>
            </motion.div>

          </div>

          {/* Right Panel - Booking Information */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-lg sticky top-8"
            >
              <h3 className="heading-tertiary text-neutral-charcoal mb-6">Booking Information</h3>
              
              {/* Price */}
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-primary-deep-blue mb-2">
                  {formatPrice(tour.price)}
                </div>
                <div className="body-small text-secondary-slate">per person</div>
              </div>

              {/* Registration Deadline */}
              {tour.deadline && (
                <div className="mb-6 p-4 bg-secondary-cream rounded-lg">
                  <div className="text-center">
                    <div className="body-medium font-semibold text-neutral-charcoal mb-1">
                      Registration Deadline
                    </div>
                    <div className={`body-large ${isUrgent ? 'text-error' : 'text-secondary-slate'}`}>
                      {formatDate(tour.deadline)}
                    </div>
                    {isUrgent && !isExpired && (
                      <div className="text-sm text-error mt-1">
                        {daysToDeadline} day{daysToDeadline !== 1 ? 's' : ''} remaining
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tour Details */}
              <div className="space-y-4 mb-6">
                {/* Dates - Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Calendar size={20} className="text-primary-deep-blue flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="body-small font-semibold text-neutral-charcoal">Departure</div>
                      <div className="body-medium text-secondary-slate truncate">
                        {formatDate(tour.departureDate)}
                      </div>
                    </div>
                  </div>
                  
                  {tour.returnDate && (
                    <div className="flex items-center space-x-3">
                      <Calendar size={20} className="text-primary-deep-blue flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="body-small font-semibold text-neutral-charcoal">Return</div>
                        <div className="body-medium text-secondary-slate truncate">
                          {formatDate(tour.returnDate)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-3">
                  <Clock size={20} className="text-primary-deep-blue flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="body-small font-semibold text-neutral-charcoal">Duration</div>
                    <div className="body-medium text-secondary-slate">
                      {tour.durationDays}D/{tour.durationNights}N
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {tour.brochureUrl && (
                  <a
                    href={getBrochureDownloadUrl(tour.brochureUrl)}
                    download
                    className="btn btn-secondary w-full inline-flex items-center justify-center"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download size={20} className="mr-2" />
                    Download Brochure
                  </a>
                )}
                <button
                  className="btn btn-primary w-full"
                  onClick={() => {
                    navigate(`/registration?tourId=${tour.tourId}`);
                  }}
                >
                  Register Now
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-6 pt-6 border-t border-neutral-medium-gray">
                <div className="text-center space-y-3">
                  <div className="body-small text-secondary-slate">Need help?</div>
                  <Link to="/contact" className="text-primary-deep-blue hover:underline block">
                    Contact our travel experts
                  </Link>
                  <WhatsAppGroupButton size="sm" fullWidth />
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TourDetailsPage;
